import { round, uniq } from 'lodash';
import { Box, styled, Typography } from '@mui/material';
import Avatar from 'further-ui/components/Avatar';
import Button from 'components/Button';
import { Column, Row } from 'components/Layout';
import PageFilters from 'components/new/PageFilters';
import SearchField from 'further-ui/components/SearchField';
import { useLayoutContext } from 'contexts/LayoutContext';
import BasicSelect from 'further-ui/components/BasicSelect';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { usePaginatedFilters } from 'further-ui/hooks';
import {
  CalendarDay,
  calculateLcyValue,
  dateToLabelAltFormat,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { revaluation as rules } from 'further-ui/validations';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import api from 'lib/trpcClient';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import CompanyRevaluationRow from './CompanyRevaluationRow';
import useBulkRevaluationsSort from './useBulkRevaluationsSort';
import BulkReviewRevaluations from './BulkReviewRevaluations';
import FullPageDrawer from 'components/new/FullPageDrawer';
import BulkRevaluationsDrawerTitle from './BulkRevaluationsDrawerTitle';
import BulkFxRates from './BulkFxRates';

const FilterContainer = styled(Box)({
  display: 'flex',
  width: '350px',
  flexDirection: 'column',
  alignItems: 'stretch',
});

enum Steps {
  Create = 0,
  Review = 1,
}

type RevaluationTab = 'total' | 'perClass';

const pageId = 'bulk-revaluations';

type Props = {
  onExit: () => void;
};

const BulkRevaluations: React.FC<Props> = ({ onExit }) => {
  const { vehicle } = useLayoutContext();
  const [activeStep, setActiveStep] = useState<Steps>(Steps.Create);
  const { periodId } = useParams<{ periodId?: string }>();

  const { pagination, filters, handleChangeFilters } = usePaginatedFilters<
    { search: string },
    'name'
  >(pageId, {
    defaultFilters: {
      search: '',
    },
    defaultOrder: {
      orderBy: 'name',
      order: 'asc',
    },
  });

  const { sortSelectOptions, sortSelectValue, handleChangeSort } =
    useBulkRevaluationsSort(pagination);

  const { data: periodData } = api.accountingPeriod.getPeriodById.useQuery(
    { vehicleId: vehicle?._id ?? '', id: periodId ?? '' },
    { enabled: !!periodId && !!vehicle?._id },
  );

  const periodEndDate = periodData?.periodEnd?.toUTCDate();

  const { data, isFetching } = api.lpCompany.list.useQuery({
    vehicleId: vehicle?._id ?? '',
    ...pagination,
    searchQuery: filters.search,
    periodEndDate: periodData?.periodEnd?.toString(),
  });

  const [expandedCompanyIds, setExpandedCompanyIds] = useState<Set<string>>(
    new Set(),
  );

  const [collectedData, setCollectedData] = useState<
    Record<
      string,
      {
        formData: rules.RevaluationFormType;
        assetId: string;
        assetName: string;
        name: string;
        companyLogo?: string | null;
      }
    >
  >({});
  const [selectedTabsByCompanyId, setSelectedTabsByCompanyId] = useState<
    Record<string, RevaluationTab>
  >({});

  const [globalFxRates, setGlobalFxRates] = useState<
    Record<string, CurrencyRateDetails>
  >({});

  const companyRows = (data?.results ?? []).map((company) => ({
    id: company._id,
    ...company,
  }));

  const uniqueNonLcyCurrencies = uniq(
    companyRows
      .flatMap((company) => company.holdingClassInvestments ?? [])
      .map((hci) => hci.currency)
      .filter(
        (currency): currency is CurrencyCode =>
          !!currency && currency !== vehicle?.baseCurrency,
      ),
  ).sort();

  const isWaitingForRates = uniqueNonLcyCurrencies.some(
    (currency) => !globalFxRates[currency],
  );

  const handleGlobalRateChange = (
    currency: CurrencyCode,
    rate: CurrencyRateDetails,
  ) => {
    setGlobalFxRates((prev) => ({ ...prev, [currency]: rate }));
    const nonLcyCompanyIds = (data?.results ?? [])
      .filter((company) =>
        company.holdingClassInvestments?.some(
          (hci) => hci.currency === currency,
        ),
      )
      .map((company) => company._id);
    setExpandedCompanyIds((prev) => new Set([...prev, ...nonLcyCompanyIds]));
  };

  const handleDataChange = (
    companyId: string,
    formData: rules.RevaluationFormType,
    assetId: string,
    assetName: string,
    name: string,
    companyLogo?: string | null,
  ) => {
    setCollectedData((prev) => ({
      ...prev,
      [companyId]: { formData, assetId, assetName, name, companyLogo },
    }));
  };

  const handleRevaluationTabChange = (
    companyId: string,
    tab: RevaluationTab,
  ) => {
    setSelectedTabsByCompanyId((current) => ({
      ...current,
      [companyId]: tab,
    }));
  };

  const getNewHoldValueLcy = (company: (typeof companyRows)[number]) => {
    const formData = collectedData[company._id]?.formData;
    if (formData) {
      const currency = formData.assetCurrency;
      const isNonLcy = currency !== vehicle?.baseCurrency;
      if (isNonLcy) {
        const rate = globalFxRates[currency];
        return rate
          ? calculateLcyValue(formData.currentHoldValue ?? 0, rate)
          : company.currentHoldValueLcy;
      }
      return formData.currentHoldValue ?? company.currentHoldValueLcy;
    }
    const nonLcyCurrency = company.holdingClassInvestments?.find(
      (hci) => hci.currency && hci.currency !== vehicle?.baseCurrency,
    )?.currency;
    if (nonLcyCurrency && globalFxRates[nonLcyCurrency]) {
      return calculateLcyValue(
        company.currentHoldValueTcy,
        globalFxRates[nonLcyCurrency],
      );
    }
    return company.currentHoldValueLcy;
  };

  const columns: Array<TableColumn<(typeof companyRows)[number]>> = [
    {
      label: 'Asset',
      key: 'name',
      render: (company) => (
        <Row centered spacing="xs">
          <Avatar
            src={company.companyLogo ?? undefined}
            text={company.name}
            size="lg"
            color="random"
          />
          <div>{company.name}</div>
        </Row>
      ),
    },
    {
      label: 'Current hold value',
      key: 'currentHoldValueLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(company.currentHoldValueLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
    {
      label: 'New hold value',
      key: 'newHoldValueLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(getNewHoldValueLcy(company), {
          currency: vehicle?.baseCurrency,
        }),
    },

    {
      label: 'Average unit price',
      key: 'currentNumberOfUnits',
      disableSorting: true,
      render: (company) => {
        if (!company.currentNumberOfUnits) return '--';
        return numberToCurrencyString(
          company.currentHoldValueLcy / company.currentNumberOfUnits,
          { currency: vehicle?.baseCurrency },
        );
      },
    },
    {
      label: 'Units held',
      key: 'totalNumberOfUnits',
      disableSorting: true,
      render: (company) => numberToDisplayString(company.currentNumberOfUnits),
    },
    {
      label: 'Revaluation date',
      key: 'latestRevaluationDate',
      disableSorting: true,
      render: (company) => dateToLabelAltFormat(company.latestRevaluationDate),
    },
  ];

  const hasChangedRevaluations = companyRows.some(
    (company) =>
      round(getNewHoldValueLcy(company), 2) !==
      round(company.currentHoldValueLcy, 2),
  );

  if (activeStep === Steps.Review && vehicle) {
    return (
      <BulkReviewRevaluations
        collectedData={collectedData}
        vehicle={vehicle}
        periodId={periodId}
        onBack={() => setActiveStep(Steps.Create)}
        onExit={onExit}
      />
    );
  }

  return (
    <FullPageDrawer
      title={
        <BulkRevaluationsDrawerTitle
          title="Asset revaluations"
          activeStep={activeStep}
          onClose={onExit}
        />
      }
      onClose={onExit}
      innerScroll
      open
      size="lg"
      actions={
        <Row fullWidth justify="space-between">
          <Button
            variant="outlined"
            size="large"
            color="primary"
            onClick={onExit}
          >
            Exit
          </Button>
          <Box sx={{ flex: 1 }} />
          <Row spacing="md">
            <Button
              size="large"
              variant="contained"
              color="primary"
              disabled={!hasChangedRevaluations}
              onClick={() => setActiveStep(Steps.Review)}
            >
              Continue
            </Button>
          </Row>
        </Row>
      }
    >
      <Column fullWidth spacing="md">
        <Column spacing="lg">
          <Column>
            <Typography variant="h5">Review asset valuations</Typography>
            <Typography variant="body2">
              {`Review and update asset valuations. Where the holding is not in ${vehicle?.baseCurrency}, it will be automatically revalued based on live exchange rates.`}
            </Typography>
          </Column>
          <PageFilters justify="space-between">
            <FilterContainer>
              <SearchField
                name="search"
                placeholder="Search by company"
                value={filters.search}
                fullWidth
                onSearch={(search) => {
                  handleChangeFilters({ search });
                }}
                allowEmptyQuery
                autoSearchOnDebounce
              />
            </FilterContainer>
            <FilterContainer>
              <BasicSelect
                fullWidth
                value={sortSelectValue}
                onChange={(value) => {
                  handleChangeSort(value);
                }}
                options={sortSelectOptions}
                placeholder="Sort by"
              />
            </FilterContainer>
          </PageFilters>
          {vehicle && periodEndDate && uniqueNonLcyCurrencies.length > 0 && (
            <BulkFxRates
              currencies={uniqueNonLcyCurrencies}
              vehicle={vehicle}
              date={new CalendarDay(periodEndDate)}
              globalFxRates={globalFxRates}
              onRateChange={handleGlobalRateChange}
            />
          )}
        </Column>
        <Table
          columns={columns}
          rows={isWaitingForRates ? [] : companyRows}
          isLoading={isFetching || isWaitingForRates}
          rowsTotalCount={data?.totalCount ?? 0}
          pagination={pagination}
          isExpandableRow={() => true}
          isExpandedRow={(row) => expandedCompanyIds.has(row._id)}
          onRowExpand={(row) => {
            setExpandedCompanyIds((current) => {
              const next = new Set(current);
              if (next.has(row._id)) {
                next.delete(row._id);
              } else {
                next.add(row._id);
              }
              return next;
            });
          }}
          darkExpandedContent
          getExpandedContent={(row) =>
            vehicle ? (
              <CompanyRevaluationRow
                company={row}
                vehicleId={vehicle._id}
                vehicle={vehicle}
                savedData={collectedData[row._id]?.formData}
                periodEndDate={periodEndDate}
                initialTab={selectedTabsByCompanyId[row._id] ?? 'total'}
                onTabChange={handleRevaluationTabChange}
                onDataChange={handleDataChange}
                globalFxRates={globalFxRates}
              />
            ) : null
          }
          emptyMessage="No companies found"
        />
      </Column>
    </FullPageDrawer>
  );
};

export default BulkRevaluations;
